<template>
  <div>
    <div id="accompanyingSheet" class="modal fade" tabindex="-1" role="dialog" aria-hidden="true">
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('accompanying_sheet.modal_title') }}</h5>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveSheet()" @keydown="form.onKeydown($event)">
              <fieldset :disabled="readOnly">

                <div class="form-group row">
                  <div class="col-md-6">
                    <label class="col-form-label">{{ $t('accompanying_sheet.job') }}</label>
                  </div>
                  <div class="col-md-6">
                    <input v-model="form.job" :class="{ 'is-invalid': form.errors.has('job') }" type="text" class="form-control" >
                    <form-error :form="form" field="job"/>
                  </div>
                </div>

                <div class="form-group row">
                  <div class="col-md-6">
                    <label class="col-form-label">{{ $t('accompanying_sheet.profession') }}</label>
                  </div>
                  <div class="col-md-6">
                    <button id="iscoButton" class="btn btn-primary" @click.prevent="showISCO()">{{ $t(form.isco ? form.isco : 'isco_selector.choose_an_occupation') }}</button>
                    <input v-model="form.isco" name="isco" type="hidden" >
                    <form-error :form="form" field="isco"/>
                  </div>
                </div>

                <div class="form-group row">
                  <div class="col-md-6">
                    <label class="col-form-label">{{ $t('accompanying_sheet.snapshot_category') }}</label>
                  </div>
                  <div class="col-md-6">
                    <div class="custom-control custom-radio">
                      <input id="snapshot_category_generic" v-model="form.generic_snapshot" :value="true" type="radio" class="custom-control-input">
                      <label class="custom-control-label" for="snapshot_category_generic">{{ $t('accompanying_sheet.generic') }}</label>
                    </div>
                    <div class="custom-control custom-radio">
                      <input id="snapshot_category_focused" v-model="form.generic_snapshot" :value="false" type="radio" class="custom-control-input">
                      <label class="custom-control-label" for="snapshot_category_focused">{{ $t('accompanying_sheet.focused') }}</label>
                    </div>
                    <form-error :form="form" field="generic_snapshot"/>
                  </div>
                </div>

                <div class="form-group row">
                  <div class="col-md-6">
                    <label class="col-form-label">{{ $t('accompanying_sheet.shared_with_community') }}</label>
                  </div>
                  <div class="col-md-6 vertical-center">
                    <YesNoDetails :form="form" field="shared_with_community"/>
                    <form-error :form="form" field="shared_with_community"/>
                  </div>
                </div>

                <div v-if="snapshot && ((availableContributors.length > 0 && !readOnly) || (snapshot.contributors.length > 0 && user.organization_id == snapshot.created_by.organization_id))" class="card">
                  <div class="card-header">
                    {{ $t('evaluation.snapshot.contributors') }}
                  </div>
                  <div class="card-body">
                    <div v-if="availableContributors.length > 0 || snapshot.contributors.length > 0">
                      <div v-if="availableContributors.length > 0" class="row form-group">
                        <div class="col-md-3 text-right"><label for="contributors" class="col-form-label">{{ $t('add') }}</label></div>
                        <div class="col-md-7">
                          <select id="contributors" v-model="contributor" name="contributors" class="custom-select custom-select-md">
                            <option v-for="c in availableContributors" :value="c.id">
                              {{ `${c.first_name} ${c.last_name}` }} {{ c.occupation ? `(${c.occupation})` : '' }}
                            </option>
                          </select>
                        </div>
                        <div class="col-md-2">
                          <button type="button" class="btn btn-list btn-outline-primary" @click.prevent="addContributor()"><fa icon="plus"/></button>
                        </div>
                      </div>
                      <card v-for="(author , i) in snapshot.contributors" v-if="snapshot.contributors" :key="i">
                        <button v-if="snapshot.creator_id !== author.id" type="button" class="close float-right" aria-label="Close" @click.prevent="removeContributor(author.id)">
                          <span aria-hidden="true">&times;</span>
                        </button>
                        <div class="row">
                          <div class="col-8">
                            <table class="table-sm table-responsive">
                              <tr>
                                <th>{{ $t('first_name') }}, {{ $t('last_name') }}</th>
                                <td>{{ author.first_name }}, {{ author.last_name }}</td>
                              </tr>
                              <tr>
                                <th>{{ $t('occupation') }}</th>
                                <td>{{ author.occupation }}, {{ author.specialization }}</td>
                              </tr>
                              <tr>
                                <th>{{ $t('email') }}</th>
                                <td>{{ author.email }}</td>
                              </tr>
                              <tr>
                                <th>{{ $t('location') }}</th>
                                <td>{{ author.address }}, {{ author.location }}, {{ author.postal_code }}, {{ author.country }}</td>
                              </tr>
                            </table>
                          </div>
                          <div class="col-4">
                            <p>{{ $t('accompanying_sheet.authors_types.title') }}</p>
                            <b-form-radio-group v-model="authors_types[author.id]" :name="`radioSubComponent${author.id}`" stacked>
                              <b-form-radio :value="false">{{ $t('accompanying_sheet.authors_types.occasional') }}</b-form-radio>
                              <b-form-radio :value="true">{{ $t('accompanying_sheet.authors_types.regular') }}</b-form-radio>
                            </b-form-radio-group>
                          </div>
                        </div>
                      </card>
                    </div>
                  </div>
                </div>

                <div class="card">
                  <div class="card-header">
                    {{ $t('accompanying_sheet.sources') }}
                  </div>
                  <div class="card-body">
                    <div v-for="source in sources" class="form-group row">
                      <div class="col-md-6">
                        <label class="col-form-label">{{ $t('accompanying_sheet.' + source.field.name) }}</label>
                        <small v-if="source.field.small">{{ $t('accompanying_sheet.small.' + source.field.name) }}</small>
                      </div>
                      <div class="col-md-6">
                        <YesNoDetails :form="form" :field="source.field.name" :details="source.details" :type="source.type"/>
                        <form-error :form="form" :field="source.field.name"/>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="card">
                  <div class="card-header">
                    {{ $t('accompanying_sheet.analyse_context') }}<br>
                    <small>{{ $t('accompanying_sheet.small.analyse_context') }}</small>
                  </div>
                  <div class="card-body">

                    <div class="form-group row">
                      <div class="col-md-6">
                        <p>
                          {{ $t('accompanying_sheet.main_activities') }}<br>
                          <small>{{ $t('accompanying_sheet.small.main_activities') }}</small>
                        </p>
                        <div class="row" style="position: relative">
                          <div class="col-12">
                            <label for="addactivity">{{ $t('accompanying_sheet.activity_name') }}</label>
                            <input id="addactivity" :class="[ activityError ? 'is-invalid' : null ]" v-model="activity" :placeholder="$t('accompanying_sheet.activity_name')" type="text" class="form-control" >
                            <div v-if="activityError" class="invalid-tooltip">
                              {{ $t('form.empty') }}
                            </div>
                          </div>
                        </div>
                        <div class="row" style="position: relative">
                          <div class="col-12">
                            <label for="selector">{{ $t('accompanying_sheet.frequency') }}</label>
                            <div class="row">
                              <div class="col-8">
                                <FiveSelector id="selector"
                                              ref="freq"
                                              :no-answer="false"
                                              :readonly="readOnly"
                                              :show-tips="true"
                                              :unknown="false"
                                              v-model="frequency"
                                              class="five-selector-rainbow"
                                              tips="accompanying_sheet.activities_ticks" />
                              </div>
                              <div class="col-4">
                                <button type="button" class="btn btn-list btn-outline-primary float-right" @click.prevent="addActivity()"><fa icon="plus"/></button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <table class="table table-sm w-100">
                          <tr>
                            <th>{{ $t('accompanying_sheet.activity_name') }}</th>
                            <th>{{ $t('accompanying_sheet.frequency') }}</th>
                            <th/>
                          </tr>
                          <tr v-for="(act, i) in activities">
                            <td>{{ act.name }}</td>
                            <td class="text-center">{{ act.frequency }}</td>
                            <td><button v-if="!readOnly" type="button" class="btn btn-sm btn-outline-danger" @click.prevent="removeActivity(i)"><fa icon="minus"/></button></td>
                          </tr>
                        </table>
                      </div>
                    </div>

                    <div v-for="tf in text_fields" class="form-group row">
                      <div class="col-md-6">
                        <label :for="tf.fields[0]" class="small col-form-label">{{ $t('accompanying_sheet.' + tf.name) }}</label>
                        <small v-if="tf.small">{{ $t('accompanying_sheet.small.' + tf.name) }}</small>
                      </div>
                      <div class="col-md-6">
                        <div v-for="field in tf.fields">
                          <label v-if="tf.fields.length > 1" :for="field" class="col-form-label">{{ $t('accompanying_sheet.' + field) }}</label>
                          <textarea v-model="form[field]" :id="field" class="form-control" rows="4"/>
                          <form-error :form="form" :field="field"/>
                        </div>
                      </div>
                    </div>

                    <div v-for="c in context" class="form-group row">
                      <div class="col-md-6">
                        <label class="col-form-label">{{ $t('accompanying_sheet.' + c.field.name) }}</label>
                        <small v-if="c.field.small">{{ $t('accompanying_sheet.small.' + c.field.name) }}</small>
                      </div>
                      <div class="col-md-6 vertical-center">
                        <YesNoDetails :form="form" :field="c.field.name" :details="c.details" :type="c.type"/>
                        <form-error :form="form" :field="c.field.name"/>
                      </div>
                    </div>

                  </div>
                </div>

              </fieldset>
            </form>
          </div>
          <div class="modal-footer">
            <div class="float-right">
              <div class="notifications"><AutoHideNotification ref="globalError" type="error" message="Test"/></div>
              <button v-if="!readOnly" type="button" class="btn btn-secondary" data-dismiss="modal">{{ $t('cancel') }}</button>
              <button v-if="!readOnly" type="button" class="btn btn-primary" @click="saveSheet()">{{ $t('save') }}</button>
              <button v-else type="button" class="btn btn-primary" data-dismiss="modal">{{ $t('close') }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ISCOSelectorModal id="iscoModal" ref="iscoModal" @saved="iscoSaved" @closed="iscoClosed" />
  </div>
</template>

<style>
  .modal-footer .notifications {
    display: inline-block;
    margin-right: 20px;
    line-height: 34px;
    vertical-align: top;
  }
</style>

<script>
import Vue from 'vue'
import Form from 'vform'
import axios from 'axios'
import { mapGetters } from 'vuex'

export default {
  name: 'AccompanyingSheet',

  props: {
    snapshot: { type: Object, default: null },
    readOnly: { type: Boolean, default: true } // default is restrictive
  },

  data: () => ({
    authors_types: {},
    activities: [],
    activity: '',
    activityError: false,
    frequency: 3,
    contributor: null,
    contributors: [],
    form: new Form({
      id: 0,
      evaluation_snapshot_id: 0,
      job: null,
      isco: '',
      generic_snapshot: null,
      shared_with_community: null,
      source_from_observations: null,
      observations_time_months: null,
      source_professional_advices: null,
      professional_specialization: null,
      source_enterprise_documents: null,
      source_supervisor_spoken_informations: null,
      source_descriptive_monographs: null,
      monographs_list: null,
      source_others: null,
      others_list: null,
      required_qualifications: null,
      required_knowledges: null,
      required_experiences: null,
      job_status: null,
      physical_environment: null,
      physical_positions: null,
      security_risks: null,
      main_difficulties: null,
      interactions_with_others: null,
      social_environment: null,
      responsibilities: null,
      human_supervision: null,
      technical_supervision: null,
      performances_standards: null,
      expected_performances: null,
      work_organization_freedom: null,
      possible_organization_adjustments: null
    }),
    sources: [
      { field: { name: 'source_from_observations', small: false }, details: 'observations_time_months', type: 1 },
      { field: { name: 'source_professional_advices', small: true }, details: 'professional_specialization', type: 1 },
      { field: { name: 'source_enterprise_documents', small: false }, details: null, type: 1 },
      { field: { name: 'source_supervisor_spoken_informations', small: false }, details: null, type: 1 },
      { field: { name: 'source_descriptive_monographs', small: true }, details: 'monographs_list', type: 2 },
      { field: { name: 'source_others', small: false }, details: 'others_list', type: 2 }
    ],
    text_fields: [
      { name: 'required_skills', fields: ['required_qualifications', 'required_knowledges', 'required_experiences'], small: true },
      { name: 'job_status', fields: ['job_status'], small: true },
      { name: 'physical_environment', fields: ['physical_environment'], small: true },
      { name: 'physical_positions', fields: ['physical_positions'], small: false },
      { name: 'security_risks', fields: ['security_risks'], small: false },
      { name: 'main_difficulties', fields: ['main_difficulties'], small: true },
      { name: 'interactions_with_others', fields: ['interactions_with_others'], small: false },
      { name: 'social_environment', fields: ['social_environment'], small: true },
      { name: 'responsibilities', fields: ['responsibilities'], small: true },
      { name: 'supervisions', fields: ['human_supervision', 'technical_supervision'], small: true }
    ],
    context: [
      { field: { name: 'performances_standards', small: false }, details: 'expected_performances', type: 2 },
      { field: { name: 'work_organization_freedom', small: false }, details: 'possible_organization_adjustments', type: 2 }
    ]
  }),

  watch: {
    snapshot: function (newV) {
      if (newV && newV.accompanying_sheet && newV.contributors) {
        this.loadSnapshot(newV)
        this.selectFirstAvailableContributor()
      } else {
        this.reset()
        this.form.creator_id = this.user.id
      }
    }
  },

  mounted () {
    if (this.snapshot) { this.loadSnapshot(this.snapshot) }
    this.loadPotentialContributors()
  },

  computed: {
    availableContributors: function () {
      let copy = this.contributors.slice()
      if (this.snapshot) {
        let ids = copy.map(x => x.id)
        for (var i = this.snapshot.contributors.length - 1; i >= 0; i--) {
          let c = this.snapshot.contributors[i]
          var index = ids.indexOf(c.id)
          if (index >= 0) {
            copy.splice(index, 1)
            ids.splice(index, 1)
          }
        }
      }
      return copy
    },

    ...mapGetters({
      user: 'auth/user'
    })
  },

  methods: {
    addActivity () {
      this.activityError = this.activity.isEmptyOrWhiteSpace()
      if (!this.activityError) {
        this.activities.push({
          snapshot_id: this.snapshot.id,
          name: this.activity,
          frequency: this.frequency
        })
        this.activity = ''
        this.frequency = 3
        this.$refs.freq.selected = 3
      }
    },

    addContributor () {
      let id = this.contributor

      if (id > 0) {
        let index = this.contributors.map(x => x.id).indexOf(id)

        if (this.snapshot && index >= 0) {
          this.authors_types[this.contributors[index].id] = null
          let c = this.contributors[index]
          this.snapshot.contributors.push(c)
          c.pivot = { skilled_writer: null }

          this.selectFirstAvailableContributor()
        }
      }
    },

    authorStatusUpdate (idx, val) {
      Vue.set(this.authors_types, idx, val)
    },

    hide () {
      $('#accompanyingSheet').modal('hide')
    },

    iscoClosed () {
      $('#accompanyingSheet').modal('show')
    },

    iscoSaved (occupation) {
      this.form.isco = occupation
    },

    async loadPotentialContributors () {
      await axios.get('/api/contributors')
        .then((response) => {
          this.contributors = response.data

          // Select first contributor.
          this.selectFirstAvailableContributor()
        })
        .catch((error) => {
          console.error(error)
        })
    },

    loadSnapshot (snapshot) {
      this.authors_types = {}

      // Do not load contributors if not same organization
      if (this.user.organization_id == this.snapshot.created_by.organization_id) {
        for (var i = 0; i < snapshot.contributors.length; i++) { this.authors_types[snapshot.contributors[i].id] = snapshot.contributors[i].pivot.skilled_writer != null ? (!!snapshot.contributors[i].pivot.skilled_writer) : null }
      }
      // if (!snapshot.contributors.map((x) => x.id).includes(this.user.id))
      //   snapshot.contributors.push(this.user);

      if (snapshot.accompanying_sheet) {
        // Load form values.
        for (var prop in snapshot.accompanying_sheet) {
          if (snapshot.accompanying_sheet.hasOwnProperty(prop)) {
            var val = snapshot.accompanying_sheet[prop]
            if (!prop.includes('id')) { val = val == 0 ? false : val == 1 ? true : val }

            this.form[prop] = val
          }
        }

        this.$refs.iscoModal.select(this.form.isco)

        let activities = snapshot.accompanying_sheet.activities
        if (activities && activities.length > 0) { this.activities = activities }
      }
    },

    async removeActivity (i) {
      if (i < this.activities.length) {
        if (this.activities[i].hasOwnProperty('id')) { await axios.delete('/api/evaluations/activities', { params: { id: this.activities[i].id } }) }
        this.activities.splice(i, 1)
      }
    },

    removeContributor (id) {
      if (this.snapshot) {
        const index = this.snapshot.contributors.map(x => x.id).indexOf(id)

        if (index >= 0) {
          this.snapshot.contributors.splice(index, 1)
          delete this.authors_types[id]
        }
      }

      this.selectFirstAvailableContributor()
    },

    reset () {
      this.authors_types = {}
      this.form.reset()
      this.activity = ''
      this.activities = []
      this.$refs.iscoModal.select(null)
    },

    async saveSheet () {
      let response = null

      this.form.evaluation_snapshot_id = this.snapshot.id
      this.form.isco = this.$refs.iscoModal.occupation

      try {
        if (this.form.id > 0) {
          response = await this.form.patch(`/api/evaluations/sheets/${this.form.id}`)
        } else {
          this.form.creator_id = this.user.id
          response = await this.form.post('/api/evaluations/sheets')
        }

        if (response.status === 200 || response.status === 201) {
          var sheet_id = response.data.id
          this.form.id = sheet_id

          this.updateActivities(sheet_id)
          const authors = this.updateAuthorsStatus()

          this.$emit('saved', { 'sheet': response.data, 'authors': authors })
          this.hide()
        } else {
          console.error(response)
        }
      } catch (e) {
        // Show error message notification
        var globalError = this.$refs.globalError
        globalError.displayNotification(this.$t('accompanying_sheet.errors.error_in_form'), 'error')

        console.log(e)
      }
    },

    selectFirstAvailableContributor () {
      if (this.availableContributors.length > 0) {
        this.contributor = this.availableContributors[0].id
      }
    },

    show (edit) {
      $('#accompanyingSheet').modal('show')
      $('#accompanyingSheet').scrollTop(0)
    },

    showISCO () {
      // $('#accompanyingSheet').modal('hide');
      $('#iscoModal').modal('show')
    },

    updateActivities (id) {
      // Abort if no activities were given.
      if (this.activities.length == 0) {
        return
      }

      // Add accompanying sheet's id to activities.
      for (var i = 0; i < this.activities.length; i++) {
        this.activities[i].accompanying_sheet_id = id
      }

      // Patch.
      axios.patch('/api/evaluations/activities', {
        activities: this.activities
      })
        .catch(function (error) {
          console.error(error)
        })
    },

    updateAuthorsStatus () {
      let skilled = []

      // Transform
      // { author id : skilled ? } to
      // [{ user id : author id, skilled ?, snapshot id }]
      for (let id in this.authors_types) {
        if (this.authors_types.hasOwnProperty(id)) {
          skilled.push({
            user_id: id,
            skilled_writer: this.authors_types[id],
            evaluation_snapshot_id: this.snapshot.id
          })
        }
      }

      let authors = []

      if (skilled.length > 0) {
        axios.patch('/api/evaluations/authors', {
          authors: skilled
        }).then((response) => {
          authors = authors
          this.snapshot.contributors.forEach(c => c.pivot.skilled_writer = this.authors_types[c.id])
        })
          .catch(function (error) {
            console.error(error)
          })
      }

      return authors
    }
  }
}
</script>
<style scoped>
.card-header {
  font-weight: bold;
}

.category-title {
  width: 100%;
  font-size: 1.2em;
  text-align: center;
  background-color: lightgrey;
  font-weight: bold;
}

.vertical-center {
  min-height: 100%;  /* Fallback for browsers do NOT support vh unit */
  display: flex;
  align-items: center;
}

#accompanyingSheet .modal-body {
  max-height: calc(100vh - 210px);
  overflow-y: scroll;
}

#iscoButton {
  max-width: 100%;
  overflow: auto;
  text-overflow: ellipsis;
}
</style>



// WEBPACK FOOTER //
// ../../../../resources/js/components/AccompanyingSheet.vue